import { openDB, DBSchema, IDBPDatabase } from "idb"
import {
    CATEGORIES_STORE_NAME,
    ITEMS_STORE_NAME,
    CACHE_EXPIRATION_MS,
    DB_CACHE_NAME,
    PROMOTIONS_STORE_NAME,
    LIST_ITEMS_STORE_NAME,
    STATUS_STORE_NAME,
} from "@/src/constants/defaults"

interface DefaultSchema {
    key: string
    value: {
        data: any
        timestamp: number
    }
}

interface CacheDB extends DBSchema {
    [CATEGORIES_STORE_NAME]: DefaultSchema
    [ITEMS_STORE_NAME]: DefaultSchema
    [PROMOTIONS_STORE_NAME]: DefaultSchema
    [LIST_ITEMS_STORE_NAME]: DefaultSchema
    [STATUS_STORE_NAME]: DefaultSchema
}

export type StoreName = typeof CATEGORIES_STORE_NAME
    | typeof ITEMS_STORE_NAME
    | typeof PROMOTIONS_STORE_NAME
    | typeof LIST_ITEMS_STORE_NAME
    | typeof STATUS_STORE_NAME

export default class CacheHelper {
    public static getCacheDB = () => {
        let database: Promise<IDBPDatabase<CacheDB>> | undefined

        try {
            if (!('indexedDB' in window)) return undefined

            database = openDB<CacheDB>(DB_CACHE_NAME, 1, {
                upgrade(db) {
                    if (!db.objectStoreNames.contains(CATEGORIES_STORE_NAME)) {
                        db.createObjectStore(CATEGORIES_STORE_NAME)
                    }
                    if (!db.objectStoreNames.contains(ITEMS_STORE_NAME)) {
                        db.createObjectStore(ITEMS_STORE_NAME)
                    }
                    if (!db.objectStoreNames.contains(PROMOTIONS_STORE_NAME)) {
                        db.createObjectStore(PROMOTIONS_STORE_NAME)
                    }
                    if (!db.objectStoreNames.contains(LIST_ITEMS_STORE_NAME)) {
                        db.createObjectStore(LIST_ITEMS_STORE_NAME)
                    }
                    if (!db.objectStoreNames.contains(STATUS_STORE_NAME)) {
                        db.createObjectStore(STATUS_STORE_NAME)
                    }
                },
            })
        }
        catch (e) {
            const error = e as Error

            if (error.name === 'UnknownError')
                console.error('Permissão negada para acesso ao Cache IndexedDB:', error);
            else
                console.error('Erro ao acessar o Cache IndexedDB:', error);

            database = undefined
        }

        return database
    }

    public static generateCacheKey(name: string, unique?: string) {
        if (!unique)
            return name
        return `${name}-${unique}`
    }

    public static async cleanupExpiredCaches(storeName: StoreName) {
        const db = await this.getCacheDB()

        if (!db || !db.objectStoreNames.contains(storeName))
            return

        const tx = db.transaction(storeName, "readwrite")
        const store = tx.objectStore(storeName)
        const now = Date.now()

        for await (const cursor of store) {
            const value = cursor.value
            if (value?.timestamp && now - value.timestamp > CACHE_EXPIRATION_MS) {
                await cursor.delete()
            }
        }

        await tx.done
    }

    public static async deleteDatabase(dbName: string): Promise<void> {
        try {
            if (!('indexedDB' in window)) return

            return new Promise((resolve, reject) => {
                const request = indexedDB.deleteDatabase(dbName)

                request.onsuccess = () => {
                    resolve()
                }

                request.onerror = () => {
                    console.error(`Erro ao deletar o banco "${dbName}":`, request.error)
                    reject(request.error)
                }
            })
        }
        catch (e) {
            console.error(e)
        }
    }
}